import type { EmailAttachment, EmailConversation, EmailMessage, EmailMessageSummary } from "../../lib/api/email";

const ALLOWED_MIME_TYPES = new Set([
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
]);

export interface MailboxConversationGroup {
  conversationId: string;
  messages: EmailMessageSummary[];
}

export function isAllowedEmailAttachment(attachment: EmailAttachment): boolean {
  return ALLOWED_MIME_TYPES.has(attachment.mimeType.toLowerCase());
}

export function formatEmailDate(value: string): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

const PREVIEW_WHITESPACE = /[\s\u00A0\u1680\u2000-\u200D\u2028\u2029\u202F\u205F\u2060\u3000\uFEFF]+/g;

/** Removes active content from mailbox HTML before it is rendered. */
export function sanitizeEmailHtml(html: string): string {
  return html
    .replace(/<(script|iframe|object|embed|form|meta|base|link)\b[^>]*>[\s\S]*?<\/\1>/gi, "")
    .replace(/<(script|iframe|object|embed|form|meta|base|link)\b[^>]*\/?>/gi, "")
    .replace(/\s+on[a-z]+\s*=\s*(['"]).*?\1/gi, "")
    .replace(/\s+on[a-z]+\s*=\s*[^\s>]+/gi, "")
    .replace(/\s(href|src)\s*=\s*(['"])\s*javascript:[\s\S]*?\2/gi, " $1=$2#$2")
    .replace(/<a\b([^>]*)>/gi, (_match, attrs: string) => {
      const cleaned = attrs
        .replace(/\s+target\s*=\s*(['"]).*?\1/gi, "")
        .replace(/\s+rel\s*=\s*(['"]).*?\1/gi, "");
    return `<a${cleaned} target="_blank" rel="noopener noreferrer">`;
  });
}

/** Wraps mailbox HTML in its own document so message styles cannot restyle the app. */
export function emailHtmlDocument(html: string): string {
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank"><style>html,body{margin:0;padding:0;}img{max-width:100%;height:auto;}table{max-width:100%;}</style></head><body>${sanitizeEmailHtml(html)}</body></html>`;
}

export function emailPreview(message: { preview?: string; bodyText?: string }, maxLength = 140): string {
  const source = (message.preview ?? message.bodyText ?? "")
    .replace(/&nbsp;/gi, " ")
    .replace(PREVIEW_WHITESPACE, " ")
    .trim();
  if (source.length <= maxLength) return source;
  return `${source.slice(0, maxLength).trimEnd()}…`;
}

export const EMAIL_BODY_UNAVAILABLE =
  "This email body could not be loaded. It may be in another folder or no longer available.";

export function visibleEmailHtml(bodyHtml?: string | null): string | null {
  const html = bodyHtml?.trim();
  return html || null;
}

export function threadAnchor(
  messages: Array<{ providerMessageId: string; receivedAt: string; folderId?: string | null }>,
): { messageId: string; folderId?: string } | null {
  const latest = [...messages].sort((left, right) => Date.parse(right.receivedAt) - Date.parse(left.receivedAt))[0];
  if (!latest) return null;
  return { messageId: latest.providerMessageId, folderId: latest.folderId || undefined };
}

export function folderLabel(
  folders: Array<{ id: string; displayName: string }>,
  folderId?: string | null,
): string | null {
  if (!folderId) return null;
  return folders.find((folder) => folder.id === folderId)?.displayName ?? folderId;
}

export function shouldRequestNextEmailPage(nextCursor?: string | null, isPaging = false): boolean {
  return Boolean(nextCursor) && !isPaging;
}

export function emailBodyStatus(input: {
  threadId: string | null;
  loading: boolean;
  hasMessage: boolean;
}): "prompt" | "loading" | "ready" | "unavailable" {
  if (!input.threadId) return "prompt";
  if (input.hasMessage) return "ready";
  if (input.loading) return "loading";
  return "unavailable";
}

export function isFullEmailMessage(message: EmailMessageSummary): message is EmailMessage {
  return "bodyText" in message && typeof message.bodyText === "string";
}

/** Keeps every loaded email and groups replies so a chain can be opened. */
export function groupMessagesByConversation(messages: EmailMessageSummary[]): MailboxConversationGroup[] {
  const groups = new Map<string, EmailMessageSummary[]>();
  const order: string[] = [];
  for (const message of messages) {
    const key = message.conversationId || message.providerMessageId;
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key)?.push(message);
  }
  return order.map((conversationId) => ({
    conversationId,
    messages: groups.get(conversationId) ?? [],
  }));
}

export function orderMessagesChronologically<T extends { receivedAt: string }>(messages: T[]): T[] {
  return [...messages].sort((left, right) => Date.parse(left.receivedAt) - Date.parse(right.receivedAt));
}

export function filterSelectedMessages<T extends { providerMessageId: string; receivedAt: string }>(
  messages: T[],
  selectedIds: Iterable<string>,
): T[] {
  const ids = new Set(selectedIds);
  return orderMessagesChronologically(messages.filter((message) => ids.has(message.providerMessageId)));
}

export function buildThreadText(
  messages: Array<{
    from: string | null;
    to: string[];
    receivedAt: string;
    subject: string;
    bodyText: string;
  }>,
): string {
  return orderMessagesChronologically(messages)
    .map((message) =>
      [
        `From: ${message.from ?? "Unknown"}`,
        `To: ${message.to.join(", ") || "Unknown"}`,
        `Date: ${formatEmailDate(message.receivedAt)}`,
        `Subject: ${message.subject}`,
        "",
        message.bodyText,
      ].join("\n"),
    )
    .join("\n\n---\n\n");
}

export function attachmentsForMessages(messages: Array<{ attachments: EmailAttachment[] }>): EmailAttachment[] {
  return messages.flatMap((message) => message.attachments).filter(isAllowedEmailAttachment);
}

export function buildSelectedConversation(messages: EmailMessage[]): EmailConversation {
  const ordered = orderMessagesChronologically(messages);
  const newest = [...ordered].sort((left, right) => Date.parse(right.receivedAt) - Date.parse(left.receivedAt))[0];
  const conversationIds = new Set(ordered.map((message) => message.conversationId));
  const singleConversation = conversationIds.size === 1;
  return {
    conversationId: singleConversation ? ordered[0]?.conversationId ?? "selected" : "selected",
    subject: singleConversation ? newest?.subject ?? "(no subject)" : "Selected emails",
    participants: [
      ...new Set(
        ordered.flatMap((message) => [message.from, ...message.to].filter((value): value is string => Boolean(value))),
      ),
    ],
    messages: ordered,
  };
}
